<template>
  <div class="pp-bar" :class="{ complete }">
    <span class="pp-title">🔮 Your bracket</span>
    <span class="pp-stat">{{ score.picksMade }}/8 picked</span>
    <span v-if="score.decidedWithPick" class="pp-stat">
      <b class="pp-good">{{ score.correct }}</b><span class="pp-dim">/{{ score.decidedWithPick }} right</span>
      · <b class="pp-good">{{ score.points }}</b><span class="pp-dim"> pts</span>
    </span>
    <span v-else class="pp-hint">Pick a winner on each cell — your pick locks when that match starts.</span>

    <span v-if="sim && sim.decided" class="pp-vs" :class="sim.tied ? 'tie' : (sim.beat ? 'win' : 'lose')"
          title="How you're doing against the battle sim's picks, head-to-head">
      🤖 vs the sim: <b>{{ sim.you }}–{{ sim.sim }}</b>
      {{ sim.tied ? '(dead even)' : (sim.beat ? '— beating the AI!' : '— AI ahead') }}
    </span>

    <span v-if="lifetime && lifetime.tournaments" class="pp-life" title="Your all-time prediction record (saved in this browser)">
      📊 {{ lifetime.tournaments }} bracket{{ lifetime.tournaments > 1 ? 's' : '' }} · {{ lifetimePct }}%
      · 🤖 {{ lifetime.beatSim }}–{{ lifetime.lostSim }}{{ lifetime.tieSim ? '–' + lifetime.tieSim : '' }}
      <span v-if="lifetime.streak > 1" class="pp-streak">· 🔥 {{ lifetime.streak }} streak</span>
    </span>

    <template v-if="complete">
      <span class="pp-final">{{ resultLine }}</span>
      <button class="pp-share" @click="share">{{ linkCopied ? '✓ Copied' : '🔗 Share' }}</button>
    </template>
  </div>
</template>

<script>
// Scoreboard for the Predict-the-Bracket game: running score, the vs-sim
// head-to-head, an all-time lifetime line, and a shareable result on completion.
import { shareText } from '../lib/predictions.js';
import shareLink from '../mixins/shareLink.js';

export default {
  name: 'PredictPanel',
  mixins: [shareLink],
  props: {
    score: { type: Object, required: true },   // scorePredictions(...) result
    sim: { type: Object, default: null },       // simRecord(...) result, or null
    lifetime: { type: Object, default: null },  // state.lifetime
    complete: { type: Boolean, default: false },
  },
  computed: {
    lifetimePct() { return this.lifetime && this.lifetime.total ? Math.round((this.lifetime.correct / this.lifetime.total) * 100) : 0; },
    resultLine() {
      let s = `🏁 Final: ${this.score.correct}/${this.score.decidedWithPick} · ${this.score.points} pts`;
      if (this.sim && this.sim.decided) {
        s += this.sim.tied ? ' · tied the sim' : (this.sim.beat ? ' · you beat the sim! 🎉' : ' · the sim won this one');
      }
      return s;
    },
  },
  methods: {
    share() { this.copyShareLink(shareText(this.score, this.sim)); },
  },
};
</script>

<style scoped>
.pp-bar {
  display: flex; flex-wrap: wrap; align-items: center; justify-content: center;
  gap: 4px 14px; margin: 0 auto 10px; max-width: 980px; padding: 6px 14px;
  border-radius: 8px; font-size: 13px; color: #e8e0ff;
  background: linear-gradient(180deg, rgba(124, 77, 255, 0.22), rgba(124, 77, 255, 0.1));
  border: 1px solid rgba(159, 122, 234, 0.55);
}
.pp-bar.complete { background: rgba(0, 0, 0, 0.35); border-color: rgba(255, 216, 119, 0.5); }
.pp-title { font-weight: bold; color: #d6c8ff; }
.pp-stat { white-space: nowrap; }
.pp-good { color: #b9f0c8; }
.pp-dim { opacity: 0.7; }
.pp-hint { opacity: 0.85; font-style: italic; }
.pp-vs { white-space: nowrap; }
.pp-vs.win { color: #8fe0a6; }
.pp-vs.lose { color: #f3a6a6; }
.pp-vs.tie { color: #ffe08a; }
.pp-life { white-space: nowrap; opacity: 0.9; color: #cbe6ff; }
.pp-streak { color: #ffb454; }
.pp-final { font-weight: bold; color: #ffd877; }
.pp-share {
  cursor: pointer; border: 1px solid #7fd1ff; border-radius: 5px; padding: 2px 10px;
  background: rgba(28, 110, 164, 0.6); color: #fff; font-weight: bold; font-size: 12px;
}
.pp-share:hover { background: #1C6EA4; }
</style>
